// FiltersSection.jsx
//
// Справочники (years, districts, benefits, disabilities) больше не загружаются
// здесь — они приходят пропсами из index.jsx, где загружаются один раз для
// всех трёх табов. Это убирает 9 дублирующихся запросов при открытии страницы
// (3 FiltersSection × 3 справочника).
import React, { useEffect, useState } from 'react';
import { types_places, groupAgeCategories } from './hooks/useStatisticsData';
import FilterDropdown from './FilterDropdown';

// ─── Мобильная шторка ────────────────────────────────────────────────────────
const MobileFilterDrawer = ({ isOpen, onClose, onApply, children }) => {
  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="publicstatistic-mobile-filter-overlay" onClick={onClose}>
      <div className="publicstatistic-mobile-filter-drawer" onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 4px' }}>
          <div style={{ width: 40, height: 4, borderRadius: 2, background: '#d1d5db' }} />
        </div>
        <div className="publicstatistic-mobile-filter-header">
          <h5>Фільтри</h5>
          <button className="publicstatistic-mobile-filter-close" onClick={onClose}>✕</button>
        </div>
        <div className="publicstatistic-mobile-filter-body">
          {children}
        </div>
        <div className="publicstatistic-mobile-filter-footer">
          <button className="publicstatistic-btn publicstatistic-btn-secondary" onClick={onClose}>
            Скасувати
          </button>
          <button className="publicstatistic-btn publicstatistic-btn-primary" onClick={() => { onApply(); onClose(); }}>
            Застосувати
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Основной компонент ───────────────────────────────────────────────────────
// Пропсы years/districts/benefits/disabilities/loadingRefs приходят из index.jsx.
// Компонент больше не делает собственных API-запросов.
const FiltersSection = ({
  tab,
  filters,
  onFilterChange,
  onApplyFilters,
  years = [],
  districts = [],
  benefits = [],
  disabilities = [],
  loadingRefs = false,
}) => {
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const months = [
    'Вересень', 'Жовтень', 'Листопад', 'Грудень', 'Січень', 'Лютий',
    'Березень', 'Квітень', 'Травень', 'Червень', 'Липень', 'Серпень',
  ];

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // ── Общие вспомогательные элементы ──────────────────────────────────────────
  const Sel = ({ label, field, options }) => (
    <FilterDropdown
      label={label}
      options={options}
      value={filters[field]}
      onChange={val => onFilterChange(tab, field, val)}
      mode="single"
    />
  );

  const yearOptions = years.map(o => ({ value: o.label, label: o.label }));

  // ── Мультивибір з ексклюзивним спецпунктом ──────────────────────────────────
  const ExclusiveMultiSelect = ({ label, field, options, specialValues, fallback, renderSelectedLabel }) => {
    const current = Array.isArray(filters[field]) ? filters[field] : [fallback];
    const handleChange = (val) => {
      let next;
      if (specialValues.includes(val)) {
        if (current.length === 1 && current[0] === val) return;
        next = [val];
      } else {
        const withoutSpecial = current.filter(v => !specialValues.includes(v));
        next = withoutSpecial.includes(val)
          ? withoutSpecial.filter(v => v !== val)
          : [...withoutSpecial, val];
        if (next.length === 0) next = [fallback];
      }
      onFilterChange(tab, field, next);
    };
    return (
      <FilterDropdown
        label={label}
        mode="multi"
        options={options}
        value={current}
        onChange={handleChange}
        renderSelectedLabel={renderSelectedLabel}
      />
    );
  };

  const benefitOptions = [
    { value: 'wo', label: 'Всі заявки' },
    { value: 'all', label: 'Всі пільги' },
    { divider: true },
    ...benefits.filter(b => b._id !== 'wo' && b._id !== 'all').map(b => ({ value: b._id, label: b.name })),
  ];

  const renderBenefitFilter = () => (
    <ExclusiveMultiSelect
      label="Пільга"
      field="benefit"
      specialValues={['wo', 'all']}
      fallback="wo"
      options={benefitOptions}
      renderSelectedLabel={(selected) => {
        if (!selected.length || selected.some(o => o.value === 'wo')) return 'Всі заявки';
        if (selected.some(o => o.value === 'all')) return 'Всі пільги';
        if (selected.length === 1) return selected[0].label;
        return `Обрано: ${selected.length}`;
      }}
    />
  );

  // ── Наборы фильтров по табу ──────────────────────────────────────────────────
  const renderApplicationsFilters = () => (
    <div className="ps-filter-grid">
      <Sel label="Навчальний рік" field="year" options={yearOptions} />
      <Sel
        label="Реєстрація в Києві"
        field="registration"
        options={[
          { value: 'kyiv_reg', label: 'З підтвердженою реєстрацією' },
          { value: 'all', label: 'Всі заявки' },
        ]}
      />
      <Sel
        label="Муніципальна картка"
        field="municipalCard"
        options={[
          { value: 'all', label: 'Всі заявки' },
          { value: 'kyiv_mr_parent', label: 'З наявними картками' },
        ]}
      />
      {renderBenefitFilter()}
      <Sel
        label="Створені"
        field="created"
        options={[
          { value: 'all', label: 'Всі заявки' },
          { value: 'inDay', label: 'Сьогодні' },
          { value: 'inDayPre', label: 'Вчора' },
          { value: 'inWeek', label: 'За тиждень' },
          { value: 'inWeekPre', label: 'Минулого тижня' },
          { value: 'inMonth', label: 'В цьому місяці' },
          { value: 'inMonthPre', label: 'В попередньому місяці' },
          { value: 'inQuart', label: 'В цьому кварталі' },
          { value: 'inQuartPre', label: 'В попередньому кварталі' },
          { value: 'inYear', label: 'З початку року' },
        ]}
      />
      <Sel
        label="Запрошення"
        field="invitations"
        options={[
          { value: 'all', label: 'Всі заявки' },
          { value: 'inDay', label: 'Сьогодні' },
          { value: 'inDayPre', label: 'Вчора' },
          { value: 'inWeek', label: 'За тиждень' },
          { value: 'inWeekPre', label: 'Минулого тижня' },
          { value: 'inMonth', label: 'В цьому місяці' },
          { value: 'inMonthPre', label: 'В попередньому місяці' },
          { value: 'wo', label: 'Без діючих запрошень' },
          { value: 'never', label: 'Без жодного запрошення' },
        ]}
      />
      <Sel
        label="Оформлені до садочка"
        field="enrolled"
        options={[
          { value: 'all', label: 'Всі заявки' },
          { value: 'inDay', label: 'Сьогодні' },
          { value: 'inDayPre', label: 'Вчора' },
          { value: 'inWeek', label: 'За тиждень' },
          { value: 'inWeekPre', label: 'Минулого тижня' },
          { value: 'inMonth', label: 'В цьому місяці' },
          { value: 'inMonthPre', label: 'В попередньому місяці' },
          { value: 'inQuart', label: 'В цьому кварталі' },
          { value: 'inQuartPre', label: 'В попередньому кварталі' },
          { value: 'inYear', label: 'З початку року' },
          { value: 'inPreYear', label: 'За минулий рік' },
        ]}
      />
      <Sel
        label="Переведення"
        field="moved"
        options={[
          { value: 'all', label: 'Всі заявки' },
          { value: 'with_move', label: 'Тільки за переведенням' },
          { value: 'without_move', label: 'Без заявок з переведенням' },
        ]}
      />
    </div>
  );

  const renderGroupsFilters = () => (
    <div className="ps-filter-grid">
      <Sel label="Навчальний рік" field="year" options={yearOptions} />
      <ExclusiveMultiSelect
        label="Район"
        field="district"
        specialValues={['all']}
        fallback="all"
        options={[
          { value: 'all', label: 'Всі райони' },
          ...districts.filter(d => d !== 'all').map(d => ({ value: d, label: d })),
        ]}
      />
      <ExclusiveMultiSelect
        label="Вікова категорія"
        field="ageCategory"
        specialValues={['all', 'with_nf', 'only_nf']}
        fallback="all"
        options={[
          { value: 'all', label: 'Всі категорії' },
          ...groupAgeCategories.map(c => ({ value: String(c.number), label: c.description })),
          { divider: true },
          { value: 'with_nf', label: 'Разом з нефункціональними' },
          { value: 'only_nf', label: 'Тільки нефункціональні' },
        ]}
      />
      <ExclusiveMultiSelect
        label="Тип місць в групі"
        field="placeType"
        specialValues={['all']}
        fallback="all"
        options={[
          { value: 'all', label: 'Будь-який' },
          ...types_places.map(t => ({ value: t.value, label: t.name })),
        ]}
      />
      <ExclusiveMultiSelect
        label="Тип труднощів"
        field="disabilityType"
        specialValues={['all', 'any', 'none']}
        fallback="all"
        options={[
          { value: 'all', label: 'Всі' },
          { value: 'any', label: 'Будь-які' },
          { value: 'none', label: 'Без труднощів' },
          { divider: true },
          ...disabilities
            .filter(d => d.isActive !== false)
            .map(d => ({ value: d._id, label: d.label || d.name })),
        ]}
      />
      <Sel
        label="Міжнародний договір"
        field="internationalAgreement"
        options={[
          { value: 'all', label: 'Всі' },
          { value: 'yes', label: 'Є договір' },
          { value: 'no', label: 'Немає договору' },
        ]}
      />
      {renderBenefitFilter()}
    </div>
  );

  const renderChildrenFilters = () => {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();
    const selectedYear = parseInt(filters.year?.split('→')[0].trim() || currentYear);
    let availableMonths = [...months];
    if (selectedYear === currentYear) {
      const monthIndex = currentMonth < 8 ? currentMonth + 5 : currentMonth - 7;
      availableMonths = months.slice(0, monthIndex + 1);
    }
    return (
      <div className="ps-filter-grid">
        <Sel label="Навчальний рік" field="year" options={yearOptions} />
        <Sel
          label="Реєстрація в Києві"
          field="registration"
          options={[
            { value: 'all', label: 'Всі' },
            { value: 'kyiv_reg', label: 'З підтвердженою реєстрацією' },
          ]}
        />
        <Sel
          label="Тип місць"
          field="placeType"
          options={[
            { value: 'all', label: 'Всі' },
            { value: 'standart', label: 'Повного дня' },
            { value: 'without-bed', label: 'Короткотривалого перебування' },
            { value: 'inclusive', label: 'Інклюзивне' },
          ]}
        />
        <ExclusiveMultiSelect
          label="Тип труднощів"
          field="disabilityType"
          specialValues={['all', 'any', 'none']}
          fallback="all"
          options={[
            { value: 'all', label: 'Всі' },
            { value: 'any', label: 'Будь-які' },
            { value: 'none', label: 'Без труднощів' },
            { divider: true },
            ...disabilities
              .filter(d => d.isActive !== false)
              .map(d => ({ value: d._id, label: d.label || d.name })),
          ]}
        />
        {renderBenefitFilter()}
        <Sel
          label="Станом на"
          field="asOf"
          options={availableMonths.map(m => ({ value: m, label: m }))}
        />
      </div>
    );
  };

  const renderFilters = () => {
    switch (tab) {
      case 'applications': return renderApplicationsFilters();
      case 'groups':       return renderGroupsFilters();
      case 'children':     return renderChildrenFilters();
      default:             return null;
    }
  };

  // ── Подсчёт активных фильтров для бейджа ─────────────────────────────────────
  const isFilterActive = (key, v) => {
    if (Array.isArray(v)) {
      const neutral = ['all', 'wo'];
      return !(v.length === 0 || (v.length === 1 && neutral.includes(v[0])));
    }
    return v && v !== 'all' && v !== 'wo' && v !== '';
  };

  const activeCount = Object.entries(filters).filter(([k, v]) => isFilterActive(k, v)).length;

  const activeTags = Object.entries(filters).flatMap(([key, value]) => {
    if (!isFilterActive(key, value)) return [];
    const label = getFilterLabel(key, value, benefits, districts, disabilities);
    return label ? [{ key, label }] : [];
  });

  if (loadingRefs) {
    return <div className="publicstatistic-filters-section text-center py-3">Завантаження фільтрів...</div>;
  }

  // ══ Мобильная версия ══════════════════════════════════════════════════════════
  if (isMobile) {
    return (
      <>
        <div className="ps-mobile-trigger">
          <button
            className="ps-mobile-filter-btn"
            onClick={() => setIsMobileFilterOpen(true)}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="11" y1="18" x2="13" y2="18"/>
            </svg>
            Фільтри
            {activeCount > 0 && (
              <span className="ps-mobile-filter-badge">{activeCount}</span>
            )}
          </button>

          {activeTags.length > 0 && (
            <div className="ps-mobile-tags">
              {activeTags.map(({ key, label }) => (
                <span key={key} className="ps-mobile-tag">{label}</span>
              ))}
            </div>
          )}
        </div>

        <MobileFilterDrawer
          isOpen={isMobileFilterOpen}
          onClose={() => setIsMobileFilterOpen(false)}
          onApply={() => onApplyFilters(tab)}
        >
          <div className="ps-mobile-filter-content">
            {renderFilters()}
          </div>
        </MobileFilterDrawer>
      </>
    );
  }

  // ══ Десктопная версия ══════════════════════════════════════════════════════════
  return (
    <div className="publicstatistic-filters-section">
      {renderFilters()}
      <div className="text-end mt-3">
        <button
          className="btn btn-primary publicstatistic-btn publicstatistic-btn-primary"
          onClick={() => onApplyFilters(tab)}
        >
          Відобразити
        </button>
      </div>
    </div>
  );
};

// ─── Вспомогательная функция лейблов ─────────────────────────────────────────
const getFilterLabel = (key, value, benefits, districts, disabilities) => {
  const map = {
    year:          v => `Рік: ${v}`,
    registration:  v => v === 'kyiv_reg' ? 'Реєстрація' : null,
    municipalCard: v => v === 'kyiv_mr_parent' ? 'Муніципальна картка' : null,
    benefit: v => {
      const arr = Array.isArray(v) ? v : [v];
      if (arr.length === 0 || arr.includes('wo')) return null;
      if (arr.includes('all')) return 'Всі пільги';
      const names = arr.map(id => benefits.find(b => b._id === id)?.name).filter(Boolean);
      return names.length ? names.join(', ') : null;
    },
    created:     v => `Створені: ${v}`,
    invitations: v => `Запрошення: ${v}`,
    enrolled:    v => `Оформлені: ${v}`,
    moved:       v => v === 'with_move' ? 'З переведенням' : v === 'without_move' ? 'Без переведення' : null,
    district: v => {
      const arr = Array.isArray(v) ? v : [v];
      if (arr.length === 0 || arr.includes('all')) return null;
      return `Район: ${arr.join(', ')}`;
    },
    ageCategory: v => {
      const arr = Array.isArray(v) ? v : [v];
      if (arr.length === 0 || arr.includes('all')) return null;
      if (arr.includes('only_nf')) return 'Тільки нефункціональні';
      if (arr.includes('with_nf')) return 'З нефункціональними';
      return `Вік: ${arr.join(', ')}`;
    },
    placeType: v => {
      const arr = Array.isArray(v) ? v : [v];
      if (arr.length === 0 || arr.includes('all')) return null;
      const names = arr.map(val => types_places.find(t => t.value === val)?.name).filter(Boolean);
      return names.length ? names.join(', ') : null;
    },
    disabilityType: v => {
      const arr = Array.isArray(v) ? v : [v];
      if (arr.length === 0 || arr.includes('all')) return null;
      if (arr.includes('none')) return 'Без труднощів';
      if (arr.includes('any')) return 'Будь-які труднощі';
      const names = arr.map(id => disabilities.find(d => d._id === id)?.label).filter(Boolean);
      return names.length ? `Труднощі: ${names.join(', ')}` : null;
    },
    internationalAgreement: v => v === 'yes' ? 'Є міжнар. договір' : v === 'no' ? 'Без міжнар. договору' : null,
    asOf: v => `Станом на: ${v}`,
  };
  return map[key] ? map[key](value) : null;
};

export default FiltersSection;